/**
 * Accessibility utilities for the guidance system
 */

export interface AccessibilityFeatures {
  focusManagement: {
    trapFocus: boolean;
    restoreFocus: boolean;
    initialFocus: string; // CSS selector
  };
  announcements: {
    stepChange: string;
    completion: string;
    dismissal: string;
  };
  keyboardShortcuts: {
    next: string[];
    previous: string[];
    skip: string[];
    dismiss: string[];
  };
}

/**
 * Focus trap utility for modal-like components
 */
export class FocusTrap {
  private element: HTMLElement;
  private focusableElements: HTMLElement[] = [];
  private firstFocusableElement: HTMLElement | null = null;
  private lastFocusableElement: HTMLElement | null = null;
  private previouslyFocusedElement: HTMLElement | null = null;

  constructor(element: HTMLElement) {
    this.element = element;
    this.updateFocusableElements();
  }

  /**
   * Get all focusable elements within the container
   */
  private updateFocusableElements(): void {
    const focusableSelectors = [
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'a[href]',
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable="true"]',
    ].join(', ');

    this.focusableElements = Array.from(
      this.element.querySelectorAll(focusableSelectors),
    ) as HTMLElement[];

    this.firstFocusableElement = this.focusableElements[0] || null;
    this.lastFocusableElement = this.focusableElements[this.focusableElements.length - 1] || null;
  }

  /**
   * Activate focus trap
   */
  activate(): void {
    this.previouslyFocusedElement = document.activeElement as HTMLElement;

    // Focus the first focusable element
    if (this.firstFocusableElement) {
      this.firstFocusableElement.focus();
    }

    // Add event listener for tab key
    this.element.addEventListener('keydown', this.handleKeyDown);
  }

  /**
   * Deactivate focus trap and restore previous focus
   */
  deactivate(): void {
    this.element.removeEventListener('keydown', this.handleKeyDown);

    // Restore focus to previously focused element
    if (this.previouslyFocusedElement && document.contains(this.previouslyFocusedElement)) {
      this.previouslyFocusedElement.focus();
    }
  }

  /**
   * Handle keydown events for focus trapping
   */
  private handleKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== 'Tab') return;

    // Update focusable elements in case DOM changed
    this.updateFocusableElements();

    if (this.focusableElements.length === 0) {
      event.preventDefault();
      return;
    }

    if (event.shiftKey) {
      // Shift + Tab (backward)
      if (document.activeElement === this.firstFocusableElement) {
        event.preventDefault();
        this.lastFocusableElement?.focus();
      }
    } else {
      // Tab (forward)
      if (document.activeElement === this.lastFocusableElement) {
        event.preventDefault();
        this.firstFocusableElement?.focus();
      }
    }
  };
}

/**
 * Screen reader announcements utility
 */
export class ScreenReaderAnnouncer {
  private liveRegion: HTMLElement | null = null;
  private politeRegion: HTMLElement | null = null;

  constructor() {
    this.createLiveRegions();
  }

  /**
   * Create ARIA live regions for announcements
   */
  private createLiveRegions(): void {
    // Assertive live region for important announcements
    this.liveRegion = document.createElement('div');
    this.liveRegion.setAttribute('aria-live', 'assertive');
    this.liveRegion.setAttribute('aria-atomic', 'true');
    this.liveRegion.setAttribute('class', 'sr-only');
    this.liveRegion.style.cssText = `
      position: absolute !important;
      width: 1px !important;
      height: 1px !important;
      padding: 0 !important;
      margin: -1px !important;
      overflow: hidden !important;
      clip: rect(0, 0, 0, 0) !important;
      white-space: nowrap !important;
      border: 0 !important;
    `;

    // Polite live region for less important announcements
    this.politeRegion = document.createElement('div');
    this.politeRegion.setAttribute('aria-live', 'polite');
    this.politeRegion.setAttribute('aria-atomic', 'true');
    this.politeRegion.setAttribute('class', 'sr-only');
    this.politeRegion.style.cssText = this.liveRegion.style.cssText;

    document.body.appendChild(this.liveRegion);
    document.body.appendChild(this.politeRegion);
  }

  /**
   * Announce message to screen readers (assertive)
   */
  announce(message: string): void {
    if (this.liveRegion) {
      this.liveRegion.textContent = message;

      // Clear after announcement to allow repeated announcements
      setTimeout(() => {
        if (this.liveRegion) {
          this.liveRegion.textContent = '';
        }
      }, 1000);
    }
  }

  /**
   * Announce message to screen readers (polite)
   */
  announcePolite(message: string): void {
    if (this.politeRegion) {
      this.politeRegion.textContent = message;

      // Clear after announcement
      setTimeout(() => {
        if (this.politeRegion) {
          this.politeRegion.textContent = '';
        }
      }, 1000);
    }
  }

  /**
   * Clean up live regions
   */
  destroy(): void {
    if (this.liveRegion && this.liveRegion.parentNode) {
      this.liveRegion.parentNode.removeChild(this.liveRegion);
    }
    if (this.politeRegion && this.politeRegion.parentNode) {
      this.politeRegion.parentNode.removeChild(this.politeRegion);
    }
  }
}

/**
 * Keyboard navigation utility
 */
export class KeyboardNavigationManager {
  private keyMap: Map<string, () => void> = new Map();
  private element: HTMLElement;
  private isActive: boolean = false;

  constructor(element: HTMLElement) {
    this.element = element;
  }

  /**
   * Register keyboard shortcut
   */
  registerShortcut(keys: string[], callback: () => void): void {
    keys.forEach((key) => {
      this.keyMap.set(key.toLowerCase(), callback);
    });
  }

  /**
   * Activate keyboard navigation
   */
  activate(): void {
    if (this.isActive) return;

    this.isActive = true;
    this.element.addEventListener('keydown', this.handleKeyDown);
  }

  /**
   * Deactivate keyboard navigation
   */
  deactivate(): void {
    if (!this.isActive) return;

    this.isActive = false;
    this.element.removeEventListener('keydown', this.handleKeyDown);
  }

  /**
   * Handle keydown events
   */
  private handleKeyDown = (event: KeyboardEvent): void => {
    const key = event.key.toLowerCase();
    const callback = this.keyMap.get(key);

    if (callback) {
      event.preventDefault();
      event.stopPropagation();
      callback();
    }
  };

  /**
   * Clear all shortcuts
   */
  clearShortcuts(): void {
    this.keyMap.clear();
  }
}

export { ColorContrastChecker, MotionPreferenceManager } from './accessibilityUtils.preferences';
import { MotionPreferenceManager } from './accessibilityUtils.preferences';

/**
 * Accessibility manager for guidance components
 */
export class GuidanceAccessibilityManager {
  private focusTrap: FocusTrap | null = null;
  private announcer: ScreenReaderAnnouncer;
  private keyboardManager: KeyboardNavigationManager | null = null;
  private motionCleanup: (() => void) | null = null;

  constructor() {
    this.announcer = new ScreenReaderAnnouncer();
  }

  /**
   * Initialize accessibility features for a guidance component
   */
  initialize(element: HTMLElement, options: Partial<AccessibilityFeatures> = {}): void {
    // Set up focus trap
    if (options.focusManagement?.trapFocus) {
      this.focusTrap = new FocusTrap(element);
      this.focusTrap.activate();
    }

    // Set up keyboard navigation
    this.keyboardManager = new KeyboardNavigationManager(element);

    if (options.keyboardShortcuts) {
      const shortcuts = options.keyboardShortcuts;

      if (shortcuts.next) {
        this.keyboardManager.registerShortcut(shortcuts.next, () => {
          this.announcer.announcePolite('Moving to next step');
        });
      }

      if (shortcuts.previous) {
        this.keyboardManager.registerShortcut(shortcuts.previous, () => {
          this.announcer.announcePolite('Moving to previous step');
        });
      }

      if (shortcuts.skip) {
        this.keyboardManager.registerShortcut(shortcuts.skip, () => {
          this.announcer.announce('Skipping guidance');
        });
      }

      if (shortcuts.dismiss) {
        this.keyboardManager.registerShortcut(shortcuts.dismiss, () => {
          this.announcer.announce('Closing guidance');
        });
      }
    }

    this.keyboardManager.activate();

    // Set up motion preference monitoring
    this.motionCleanup = MotionPreferenceManager.addMotionPreferenceListener((prefersReduced) => {
      if (prefersReduced) {
        element.classList.add('reduce-motion');
      } else {
        element.classList.remove('reduce-motion');
      }
    });
  }

  /**
   * Announce step change
   */
  announceStepChange(stepTitle: string, stepNumber: number, totalSteps: number): void {
    const message = `Step ${stepNumber} of ${totalSteps}: ${stepTitle}`;
    this.announcer.announce(message);
  }

  /**
   * Announce guidance completion
   */
  announceCompletion(): void {
    this.announcer.announce('Guidance completed successfully');
  }

  /**
   * Announce guidance dismissal
   */
  announceDismissal(): void {
    this.announcer.announce('Guidance dismissed');
  }

  /**
   * Clean up accessibility features
   */
  cleanup(): void {
    if (this.focusTrap) {
      this.focusTrap.deactivate();
      this.focusTrap = null;
    }

    if (this.keyboardManager) {
      this.keyboardManager.deactivate();
      this.keyboardManager = null;
    }

    if (this.motionCleanup) {
      this.motionCleanup();
      this.motionCleanup = null;
    }
  }

  /**
   * Destroy and clean up all resources
   */
  destroy(): void {
    this.cleanup();
    this.announcer.destroy();
  }
}

// Export singleton instance
export const guidanceAccessibilityManager = new GuidanceAccessibilityManager();
